import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Button,
  Modal,
  Spinner,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import { toast } from "react-toastify";
import {
  createContactInfo,
  editContactInfo,
  getContactInfo,
  deleteContactInfo,
  ContactInfo,
  setDefaultContactInfo,
} from "api/apis";

const PersonalInfo: React.FC = () => {
  document.title = "Contact Information";

  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [contactToDelete, setContactToDelete] = useState<string | null>(null);

  // Form state for new/edit
  const [formData, setFormData] = useState<ContactInfo>({
    officeNo: "",
    floor: "",
    building: "",
    landmark: "",
    street: "",
    area: "",
    gstin: "",
    pincode: "",
    email: "",
    phone: "",
    mapLink: "",
    mapIframe: "",
    isDefault: false,
  });

  // Fetch contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async (): Promise<void> => {
    try {
      setLoading(true);
      const contactsData = await getContactInfo();

      console.log("Fetched contacts data:", contactsData);

      if (contactsData) {
        // Convert to array if needed
        if (Array.isArray(contactsData)) {
          setContacts(contactsData);
        } else if (
          contactsData &&
          typeof contactsData === "object" &&
          "_id" in contactsData
        ) {
          // Single contact object
          setContacts([contactsData as ContactInfo]);
        } else {
          setContacts([]);
        }
      } else {
        setContacts([]);
      }
    } catch (error: any) {
      console.error("Error fetching contacts:", error);
      if (error.response?.status !== 404) {
        toast.error("Failed to fetch contact information");
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle input changes
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value, type } = e.target;
    const newValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    const requiredFields: (keyof ContactInfo)[] = [
      "officeNo",
      "floor",
      "building",
      "street",
      "area",
      "pincode",
      "email",
      "phone",
    ];

    requiredFields.forEach((field) => {
      if (!formData[field]?.toString().trim()) {
        newErrors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      }
    });

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (
      formData.phone &&
      !/^\d{10,15}$/.test(formData.phone.replace(/\D/g, ""))
    ) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (formData.pincode && !/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = "Pincode must be 6 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the form errors");
      return;
    }

    try {
      setSaving(true);

      if (editingId) {
        // Update existing contact - FIXED: Pass id and data separately
        const updatedContact = await editContactInfo(editingId, formData);

        // Update in local state
        setContacts(
          contacts.map((contact) =>
            contact._id === editingId ? updatedContact : contact,
          ),
        );

        toast.success("Contact information updated successfully!");
      } else {
        // Create new contact
        const newContact = await createContactInfo(formData);

        // Add to local state
        setContacts([...contacts, newContact]);

        toast.success("Contact information added successfully!");
      }

      resetForm();
    } catch (error: any) {
      console.error("Error saving contact info:", error);
      toast.error(
        error.response?.data?.error || "Failed to save contact information",
      );
    } finally {
      setSaving(false);
    }
  };

  // Start adding new contact
  const startAdding = (): void => {
    resetForm();
    setIsEditing(true);
    setEditingId(null);
  };

  // Start editing existing contact
  const startEditing = (contact: ContactInfo): void => {
    setFormData({
      officeNo: contact.officeNo || "",
      floor: contact.floor || "",
      building: contact.building || "",
      landmark: contact.landmark || "",
      street: contact.street || "",
      area: contact.area || "",
      pincode: contact.pincode || "",
      email: contact.email || "",
      gstin: contact.gstin || "",
      phone: contact.phone || "",
      mapLink: contact.mapLink || "",
      mapIframe: contact.mapIframe || "",
      isDefault: contact.isDefault || false,
    });
    setEditingId(contact._id || null);
    setIsEditing(true);
  };

  // Cancel editing/adding
  const cancelEditing = (): void => {
    resetForm();
  };

  // Reset form
  const resetForm = (): void => {
    setFormData({
      officeNo: "",
      floor: "",
      building: "",
      landmark: "",
      street: "",
      area: "",
      pincode: "",
      email: "",
      gstin: "",
      phone: "",
      mapLink: "",
      mapIframe: "",
      isDefault: false,
    });
    setEditingId(null);
    setIsEditing(false);
    setErrors({});
  };

  // Handle delete confirmation
  const confirmDelete = (id: string): void => {
    setContactToDelete(id);
    setShowDeleteModal(true);
  };

  // Handle delete
  const handleDelete = async (): Promise<void> => {
    if (!contactToDelete) return;

    try {
      await deleteContactInfo(contactToDelete);

      // Remove from local state
      setContacts(
        contacts.filter((contact) => contact._id !== contactToDelete),
      );

      setShowDeleteModal(false);
      setContactToDelete(null);

      // If we were editing the deleted contact, reset the form
      if (editingId === contactToDelete) {
        resetForm();
      }

      toast.success("Contact information deleted successfully!");
    } catch (error: any) {
      console.error("Error deleting contact info:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete contact information",
      );
    }
  };

  // Set contact as default - FIXED: Correct API call
  const setAsDefault = async (id: string): Promise<void> => {
    try {
      if (!id) return;

      if (id) {
        await setDefaultContactInfo(id);
        fetchContacts();
      }

      // Update local state
      toast.success("Default contact updated successfully!");
    } catch (error: any) {
      console.error("Error setting default contact:", error);
      toast.error("Failed to set default contact");
    }
  };

  if (loading) {
    return (
      <div
        style={{
          marginTop: "6rem",
          padding: "2rem",
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <div style={{ textAlign: "center", padding: "3rem 0" }}>
          <Spinner animation="border" variant="primary" />
          <p style={{ marginTop: "1rem" }}>Loading contact information...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: "6rem",
        padding: "2rem",
        minHeight: "100vh",
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", margin: "0 -15px" }}>
        <div style={{ flex: "0 0 100%", maxWidth: "100%", padding: "0 15px" }}>
          <Card
            style={{
              border: "none",
              borderRadius: "12px",
              boxShadow: "0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)",
            }}
          >
            <Card.Header
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1rem",
                borderBottom: "2px solid #e9ecef",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h4 style={{ margin: 0, color: "#0d6efd" }}>
                Contact Information
                <span
                  style={{
                    fontSize: "0.875rem",
                    marginLeft: "1rem",
                    color: "#6c757d",
                  }}
                >
                  {contacts.length > 0 && `(${contacts.length} addresses)`}
                </span>
              </h4>
              <div>
                {!isEditing ? (
                  <Button
                    variant="primary"
                    onClick={startAdding}
                    style={{ padding: "0.375rem 1.5rem" }}
                  >
                    <i className="fas fa-plus me-2"></i>
                    Add New Address
                  </Button>
                ) : (
                  <Button variant="outline-secondary" onClick={cancelEditing}>
                    Cancel
                  </Button>
                )}
              </div>
            </Card.Header>

            <Card.Body style={{ padding: "2rem" }}>
              {!isEditing ? (
                // Display mode - show all contacts
                contacts.length > 0 ? (
                  <Row>
                    {contacts.map((contact, index) => (
                      <Col
                        md={6}
                        key={contact._id || index}
                        style={{ marginBottom: "2rem" }}
                      >
                        <Card
                          style={{
                            border: contact.isDefault
                              ? "2px solid #0d6efd"
                              : "1px solid #dee2e6",
                            borderRadius: "0.375rem",
                            height: "100%",
                            position: "relative",
                          }}
                        >
                          {contact.isDefault && (
                            <div
                              style={{
                                position: "absolute",
                                top: "-10px",
                                right: "10px",
                                backgroundColor: "#0d6efd",
                                color: "white",
                                padding: "2px 8px",
                                borderRadius: "10px",
                                fontSize: "0.75rem",
                                fontWeight: "bold",
                              }}
                            >
                              DEFAULT
                            </div>
                          )}
                          <Card.Body>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "1rem",
                              }}
                            >
                              <h5 style={{ color: "#0d6efd", margin: 0 }}>
                                Address {index + 1}
                              </h5>
                              <div>
                                {!contact.isDefault && (
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={() =>
                                      contact._id && setAsDefault(contact._id)
                                    }
                                    style={{ marginRight: "0.5rem" }}
                                  >
                                    Set as Default
                                  </Button>
                                )}
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => startEditing(contact)}
                                  style={{ marginRight: "0.5rem" }}
                                >
                                  <i className="bi bi-pencil-square"></i> Edit
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() =>
                                    contact._id && confirmDelete(contact._id)
                                  }
                                >
                                  <i className="bi bi-trash"></i> Delete
                                </Button>
                              </div>
                            </div>

                            <Row>
                              <Col md={6}>
                                <h6
                                  style={{
                                    color: "#495057",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  Office Address
                                </h6>
                                <div>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Office No:</strong>{" "}
                                    {contact.officeNo}
                                  </p>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Floor:</strong> {contact.floor}
                                  </p>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Building:</strong>{" "}
                                    {contact.building}
                                  </p>
                                  {contact.landmark && (
                                    <p
                                      style={{
                                        marginBottom: "0.25rem",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      <strong>Landmark:</strong>{" "}
                                      {contact.landmark}
                                    </p>
                                  )}
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Street:</strong> {contact.street}
                                  </p>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Area:</strong> {contact.area}
                                  </p>
                                  <p
                                    style={{
                                      marginBottom: 0,
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Pincode:</strong> {contact.pincode}
                                  </p>
                                </div>
                              </Col>

                              <Col md={6}>
                                <h6
                                  style={{
                                    color: "#495057",
                                    marginBottom: "0.5rem",
                                  }}
                                >
                                  Contact Details
                                </h6>
                                <div style={{ marginBottom: "1rem" }}>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Email:</strong>
                                    <a
                                      href={`mailto:${contact.email}`}
                                      style={{
                                        marginLeft: "0.25rem",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {contact.email}
                                    </a>
                                  </p>
                                  <p
                                    style={{
                                      marginBottom: "0.25rem",
                                      fontSize: "0.875rem",
                                    }}
                                  >
                                    <strong>Phone:</strong>
                                    <a
                                      href={`tel:${contact.phone}`}
                                      style={{
                                        marginLeft: "0.25rem",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      {contact.phone}
                                    </a>
                                  </p>
                                  {contact.gstin && (
                                    <p
                                      style={{
                                        marginBottom: "0.25rem",
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      <strong>GSTIN:</strong> {contact.gstin}
                                    </p>
                                  )}
                                  {contact.mapLink && (
                                    <p
                                      style={{
                                        marginBottom: 0,
                                        fontSize: "0.875rem",
                                      }}
                                    >
                                      <strong>Map Link:</strong>
                                      <br />
                                      <a
                                        href={contact.mapLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ fontSize: "0.875rem" }}
                                      >
                                        View on Google Maps
                                      </a>
                                    </p>
                                  )}
                                </div>

                                {contact.mapIframe && (
                                  <div style={{ marginTop: "0.5rem" }}>
                                    <h6
                                      style={{
                                        fontSize: "0.875rem",
                                        marginBottom: "0.25rem",
                                      }}
                                    >
                                      Location Map
                                    </h6>
                                    <div
                                      dangerouslySetInnerHTML={{
                                        __html: contact.mapIframe,
                                      }}
                                      style={{
                                        maxWidth: "100%",
                                        height: "150px",
                                        borderRadius: "4px",
                                        overflow: "hidden",
                                      }}
                                    />
                                  </div>
                                )}
                              </Col>
                            </Row>

                            {contact.createdAt && (
                              <div
                                style={{
                                  marginTop: "1rem",
                                  paddingTop: "0.5rem",
                                  borderTop: "1px solid #e9ecef",
                                }}
                              >
                                <small
                                  style={{
                                    color: "#6c757d",
                                    fontSize: "0.75rem",
                                  }}
                                >
                                  Created:{" "}
                                  {new Date(
                                    contact.createdAt,
                                  ).toLocaleDateString("en-GB")}
                                  {contact.updatedAt &&
                                    contact.updatedAt !== contact.createdAt &&
                                    ` • Updated: ${new Date(contact.updatedAt).toLocaleDateString("en-GB")}`}
                                </small>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Alert
                    variant="info"
                    style={{ textAlign: "center", padding: "2rem" }}
                  >
                    <i className="fas fa-info-circle me-2"></i>
                    No contact information found. Click "Add New Address" to
                    create one.
                  </Alert>
                )
              ) : (
                // Edit/Add mode
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col lg={6}>
                      <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                        {editingId ? "Edit Address" : "Add New Address"}
                      </h5>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Office No. *</Form.Label>
                        <Form.Control
                          type="text"
                          name="officeNo"
                          value={formData.officeNo}
                          onChange={handleInputChange}
                          placeholder="e.g., Off 13/A"
                          isInvalid={!!errors.officeNo}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.officeNo}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Floor *</Form.Label>
                        <Form.Control
                          type="text"
                          name="floor"
                          value={formData.floor}
                          onChange={handleInputChange}
                          placeholder="e.g., 4th Floor"
                          isInvalid={!!errors.floor}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.floor}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Building *</Form.Label>
                        <Form.Control
                          type="text"
                          name="building"
                          value={formData.building}
                          onChange={handleInputChange}
                          placeholder="e.g., 97/101"
                          isInvalid={!!errors.building}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.building}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Landmark</Form.Label>
                        <Form.Control
                          type="text"
                          name="landmark"
                          value={formData.landmark}
                          onChange={handleInputChange}
                          placeholder="e.g., Near Mumbadevi Chambers"
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={6}>
                      <h5 style={{ color: "#0d6efd", marginBottom: "1rem" }}>
                        Address Details
                      </h5>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Street *</Form.Label>
                        <Form.Control
                          type="text"
                          name="street"
                          value={formData.street}
                          onChange={handleInputChange}
                          placeholder="e.g., Shaikh Memon Street"
                          isInvalid={!!errors.street}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.street}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Area *</Form.Label>
                        <Form.Control
                          type="text"
                          name="area"
                          value={formData.area}
                          onChange={handleInputChange}
                          placeholder="e.g., Zaveri Bazar-Kalbadevi"
                          isInvalid={!!errors.area}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.area}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Pincode *</Form.Label>
                        <Form.Control
                          type="text"
                          name="pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          placeholder="e.g., 400002"
                          maxLength={6}
                          isInvalid={!!errors.pincode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.pincode}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Email *</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="e.g., anjalgold@gmail.com"
                          isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>GSTIN</Form.Label>
                        <Form.Control
                          type="text"
                          name="gstin"
                          value={formData.gstin}
                          onChange={handleInputChange}
                          placeholder="27ABCDE1234F1Z5"
                        />
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Phone *</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="e.g., 07942694079"
                          isInvalid={!!errors.phone}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Google Maps Link</Form.Label>
                        <Form.Control
                          type="url"
                          name="mapLink"
                          value={formData.mapLink}
                          onChange={handleInputChange}
                          placeholder="e.g., https://maps.google.com/?q=19.243,72.845"
                        />
                        <Form.Text style={{ color: "#6c757d" }}>
                          Paste the full Google Maps URL for your location
                        </Form.Text>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Label>Google iFrame Link</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="mapIframe"
                          value={formData.mapIframe}
                          onChange={handleInputChange}
                          placeholder="e.g., <iframe src='https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3771.715157598572!2d72.82744551546768!3d19.043756687111237!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c8e7d6c8b5b5%3A0x6a7b7b7b7b7b7b7b!2sMumbai%2C%20Maharashtra%20400002!5e0!3m2!1sen!2sin!4v1627541234567!5m2!1sen!2sin' width='600' height='450' style='border:0;' allowfullscreen='' loading='lazy'></iframe>"
                        />
                        <Form.Text style={{ color: "#6c757d" }}>
                          Paste the embed code for Google Maps
                        </Form.Text>
                      </Form.Group>

                      <Form.Group style={{ marginBottom: "1rem" }}>
                        <Form.Check
                          type="switch"
                          id="isDefault"
                          name="isDefault"
                          label="Set as default address"
                          checked={formData.isDefault}
                          onChange={handleInputChange}
                          disabled={contacts.length === 0 && editingId === null}
                        />
                        <Form.Text style={{ color: "#6c757d" }}>
                          {contacts.length === 0 && editingId === null
                            ? "This will be automatically set as default since it's the first address"
                            : "Only one address can be set as default"}
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginTop: "1.5rem",
                      paddingTop: "1rem",
                      borderTop: "1px solid #dee2e6",
                    }}
                  >
                    <div>
                      {editingId && (
                        <Button
                          variant="outline-danger"
                          onClick={() => {
                            setContactToDelete(editingId);
                            setShowDeleteModal(true);
                          }}
                          type="button"
                        >
                          <i className="fas fa-trash me-2"></i>
                          Delete
                        </Button>
                      )}
                    </div>
                    <div>
                      <Button
                        variant="outline-secondary"
                        onClick={cancelEditing}
                        style={{ marginRight: "0.5rem" }}
                        type="button"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        disabled={saving}
                        style={{ padding: "0.375rem 1.5rem" }}
                      >
                        {saving ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              style={{ marginRight: "0.5rem" }}
                            />
                            Saving...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-save me-2"></i>
                            {editingId ? "Update Address" : "Save Address"}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
          setContactToDelete(null);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: "center" }}>
            <i
              className="fas fa-exclamation-triangle fa-3x mb-3"
              style={{ color: "#ffc107" }}
            ></i>
            <p>
              Are you sure you want to delete this address? This action cannot
              be undone.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setContactToDelete(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            <i className="fas fa-trash me-2"></i>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PersonalInfo;
